import React from "react";
import { Shapes, Ruler, Palette } from "lucide-react";

const SHAPES = [
  // Each is a tiny SVG silhouette of a window/door shape
  "M2 22V2h20v20H2zm2-2h16V4H4v16z",
  "M2 22V2h20v20H2zm2-2h16V4H4v16zm0-9h16",
  "M12 2a10 10 0 0 1 10 10v10H2V12A10 10 0 0 1 12 2z",
  "M2 22V12L12 2l10 10v10H2z",
  "M2 22V2l10 10L22 2v20H2z",
  "M4 22V4h16v18l-8-5-8 5z",
];

const COLORS = [
  { name: "Matte Black", swatch: "#1c1c1c" },
  { name: "Anthracite", swatch: "#3a3f3d" },
  { name: "Bronze", swatch: "#8e6a3a" },
  { name: "Champagne", swatch: "#c2a37a" },
  { name: "Silver Mist", swatch: "#b6b6b1" },
  { name: "Pure White", swatch: "#f4f1ea" },
  { name: "Forest Green", swatch: "#1f3a2e" },
  { name: "Custom RAL", swatch: "linear-gradient(135deg,#c9657a,#e2b880,#7faec1,#1f3a2e)" },
];

export default function Customization() {
  return (
    <section
      data-testid="customization-section"
      id="customization"
      className="relative bg-[#0f2420] text-white overflow-hidden"
    >
      {/* Subtle gold rule pattern background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #e2b880 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative container-vlx py-24 md:py-32">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="eyebrow eyebrow-light">Made to Order</span>
          <div className="mt-4 rule-gold" />
          <h2 className="heading-lg mt-8 text-[34px] md:text-[56px] leading-[1.05] text-white">
            Custom aluminum windows &amp; doors,{" "}
            <em className="italic font-light text-[#e2b880]">built to spec.</em>
          </h2>
          <p className="mt-8 text-white/70 text-[15px] md:text-[16px] leading-[1.9] font-nunito max-w-xl">
            Every ViewLux window and door is fully customizable. Choose any
            shape, size, and finish &mdash; we manufacture each opening to the
            exact dimensions and aesthetic of your project. No standard SKUs,
            no off-the-shelf compromises.
          </p>
        </div>

        {/* Three pillars */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-3 gap-px bg-[#c99658]/15">
          {/* Shape */}
          <div
            data-testid="custom-shape"
            className="bg-[#0f2420] p-8 md:p-12 group"
          >
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#e2b880] group-hover:bg-[#c99658] group-hover:text-[#0f2420] transition-colors">
                <Shapes className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]">
                01 &middot; Shape
              </span>
            </div>
            <h3 className="heading-lg mt-7 text-[26px] md:text-[30px] text-white leading-[1.15]">
              Any geometry.
            </h3>
            <p className="mt-4 text-white/65 text-[14px] leading-[1.85] font-nunito">
              Rectangles, arches, trapezoids, corner units, angled gables,
              perfect circles &mdash; if you can draw it, we can build it.
              Send us a sketch or a CAD file.
            </p>
            {/* Shape silhouettes */}
            <ul className="mt-8 grid grid-cols-6 gap-2 max-w-xs">
              {SHAPES.map((d, i) => (
                <li
                  key={i}
                  className="aspect-square border border-[#c99658]/30 flex items-center justify-center bg-[#16332d] hover:border-[#e2b880] transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="#e2b880"
                    strokeWidth="1.2"
                    aria-hidden="true"
                  >
                    <path d={d} />
                  </svg>
                </li>
              ))}
            </ul>
          </div>

          {/* Size */}
          <div
            data-testid="custom-size"
            className="bg-[#0f2420] p-8 md:p-12 group"
          >
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#e2b880] group-hover:bg-[#c99658] group-hover:text-[#0f2420] transition-colors">
                <Ruler className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]">
                02 &middot; Size
              </span>
            </div>
            <h3 className="heading-lg mt-7 text-[26px] md:text-[30px] text-white leading-[1.15]">
              Built to the inch.
            </h3>
            <p className="mt-4 text-white/65 text-[14px] leading-[1.85] font-nunito">
              From a 24&Prime; awning window to a 20+ ft sliding wall &mdash;
              every unit is manufactured to your exact opening. Thermally
              broken aluminum gives us the structural reach for oversized
              glazing without sacrificing performance.
            </p>
            {/* Ruler illustration */}
            <div className="mt-8 max-w-xs">
              <div className="relative h-12 border border-[#c99658]/30 bg-[#16332d]">
                <div className="absolute inset-x-3 inset-y-0 flex items-center justify-between">
                  {[...Array(11)].map((_, i) => (
                    <span
                      key={i}
                      className={`block w-px bg-[#e2b880]/60 ${
                        i % 5 === 0 ? "h-5" : "h-2.5"
                      }`}
                    />
                  ))}
                </div>
                <span className="absolute -bottom-6 left-3 text-[9px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]/70">
                  0 ft
                </span>
                <span className="absolute -bottom-6 right-3 text-[9px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]/70">
                  20+ ft
                </span>
              </div>
            </div>
          </div>

          {/* Color */}
          <div
            data-testid="custom-color"
            className="bg-[#0f2420] p-8 md:p-12 group"
          >
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#e2b880] group-hover:bg-[#c99658] group-hover:text-[#0f2420] transition-colors">
                <Palette className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]">
                03 &middot; Color
              </span>
            </div>
            <h3 className="heading-lg mt-7 text-[26px] md:text-[30px] text-white leading-[1.15]">
              Any finish.
            </h3>
            <p className="mt-4 text-white/65 text-[14px] leading-[1.85] font-nunito">
              Standard finishes shown below &mdash; plus the full RAL Classic
              system on request. Powder-coated, anodized, or dual-tone
              (different inside vs outside).
            </p>
            {/* Color swatches */}
            <ul className="mt-8 grid grid-cols-4 gap-2 max-w-xs">
              {COLORS.map((c, i) => (
                <li
                  key={c.name}
                  title={c.name}
                  className="group/sw aspect-square relative cursor-help"
                >
                  <div
                    className="absolute inset-0 border border-[#c99658]/40 transition-transform duration-300 group-hover/sw:scale-[1.06]"
                    style={
                      c.swatch.startsWith("linear-gradient")
                        ? { backgroundImage: c.swatch }
                        : { backgroundColor: c.swatch }
                    }
                  />
                  <span className="absolute -bottom-6 left-0 right-0 text-center text-[8.5px] tracking-[0.18em] uppercase font-raleway text-[#e2b880]/70 opacity-0 group-hover/sw:opacity-100 transition-opacity whitespace-nowrap">
                    {c.name}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-12 text-[10.5px] tracking-[0.35em] uppercase font-raleway text-[#e2b880]/60">
              + Full RAL Classic spectrum
            </p>
          </div>
        </div>

        {/* Footer CTA strip */}
        <div className="mt-16 md:mt-24 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10 pt-10 border-t border-[#c99658]/15">
          <p className="font-cormorant italic text-[#e2b880] text-[22px] md:text-[26px] leading-[1.3] max-w-md">
            Have a specific look in mind? Send us a sketch, a Pinterest board,
            or a CAD file &mdash; we&rsquo;ll quote it.
          </p>
          <a
            href="#contact"
            data-testid="customization-cta"
            className="btn-pill btn-primary !text-[11px] tracking-[0.32em]"
          >
            Start a custom quote
          </a>
        </div>
      </div>
    </section>
  );
}
